import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  Mail, Settings, FileText, Eye, Save, RefreshCw, Send, 
  CheckCircle, XCircle, AlertCircle, Loader2, Code, 
  BarChart3, Ban, Trash2, MousePointerClick, MailOpen, TrendingUp
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const MailSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [activeTab, setActiveTab] = useState('smtp');
  
  // SMTP Settings
  const [smtpSettings, setSmtpSettings] = useState({
    enabled: false,
    from_name: 'VVVSI Job Portal',
    from_email: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    use_tls: true,
    default_cc: []
  });
  const [testEmail, setTestEmail] = useState('');
  
  // Email Templates
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [showCodeEditor, setShowCodeEditor] = useState(false);
  
  // Tracking & Analytics
  const [emailStats, setEmailStats] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  const [logsFilter, setLogsFilter] = useState('');
  
  // Blocked Emails
  const [blockedEmails, setBlockedEmails] = useState([]);
  const [blockedPage, setBlockedPage] = useState(1);
  const [blockedTotalPages, setBlockedTotalPages] = useState(1);
  const [newBlockedEmail, setNewBlockedEmail] = useState('');
  const [showBlockDialog, setShowBlockDialog] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (activeTab === 'tracking') {
      fetchEmailStats();
      fetchEmailLogs();
    } else if (activeTab === 'blocked') {
      fetchBlockedEmails();
    }
  }, [activeTab, logsPage, blockedPage]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const [smtpRes, templatesRes] = await Promise.all([
        axios.get(`${API}/api/admin/email/smtp-settings`),
        axios.get(`${API}/api/admin/email/templates`)
      ]);
      setSmtpSettings(smtpRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      toast.error('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailStats = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/email/logs/stats`);
      setEmailStats(res.data);
    } catch (error) {
      console.error('Failed to fetch email stats', error);
    }
  };

  const fetchEmailLogs = async () => {
    try {
      const params = { page: logsPage, limit: 20 };
      if (logsFilter) params.email = logsFilter;
      const res = await axios.get(`${API}/api/admin/email/logs`, { params });
      setEmailLogs(res.data.data || []);
      setLogsTotalPages(res.data.total_pages || 1);
    } catch (error) {
      console.error('Failed to fetch email logs', error);
    }
  };

  const fetchBlockedEmails = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/email/blocked`, { 
        params: { page: blockedPage, limit: 20 } 
      });
      setBlockedEmails(res.data.data || []);
      setBlockedTotalPages(res.data.total_pages || 1);
    } catch (error) {
      console.error('Failed to fetch blocked emails', error);
    }
  };

  const handleSaveSmtp = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/email/smtp-settings`, smtpSettings);
      toast.success('SMTP settings saved successfully');
    } catch (error) {
      toast.error('Failed to save SMTP settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }
    setTesting(true);
    try {
      await axios.post(`${API}/api/admin/email/test-smtp`, { test_email: testEmail });
      toast.success(`Test email sent to ${testEmail}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  const handleSelectTemplate = async (template) => {
    setSelectedTemplate(template);
    setEditingTemplate({ ...template });
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/email/templates/${editingTemplate.key}`, editingTemplate);
      toast.success('Template saved successfully');
      fetchSettings();
      setSelectedTemplate(editingTemplate);
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewTemplate = async () => {
    if (!editingTemplate) return;
    try {
      const res = await axios.post(`${API}/api/admin/email/templates/${editingTemplate.key}/preview`, {});
      setPreviewHtml(res.data.html_body);
      setShowPreview(true);
    } catch (error) {
      toast.error('Failed to generate preview');
    }
  };

  const handleResetDefaults = async () => {
    if (!window.confirm('Reset all templates to defaults? This will overwrite any customizations.')) return;
    try {
      await axios.post(`${API}/api/admin/email/templates/reset-defaults`);
      toast.success('Templates reset to defaults');
      fetchSettings();
      setSelectedTemplate(null);
      setEditingTemplate(null);
    } catch (error) {
      toast.error('Failed to reset templates');
    }
  };

  const handleBlockEmail = async () => {
    if (!newBlockedEmail) return;
    try {
      await axios.post(`${API}/api/admin/email/blocked`, { 
        email: newBlockedEmail,
        reason: 'Manually blocked by admin'
      });
      toast.success('Email blocked successfully');
      setNewBlockedEmail('');
      setShowBlockDialog(false);
      fetchBlockedEmails();
      fetchEmailStats();
    } catch (error) {
      toast.error('Failed to block email');
    }
  };

  const handleUnblockEmail = async (email) => {
    if (!window.confirm(`Unblock ${email}?`)) return;
    try {
      await axios.delete(`${API}/api/admin/email/blocked/${encodeURIComponent(email)}`);
      toast.success('Email unblocked successfully');
      fetchBlockedEmails();
      fetchEmailStats();
    } catch (error) {
      toast.error('Failed to unblock email');
    }
  };

  const getTemplateIcon = (key) => {
    const icons = {
      'employer_welcome': <Mail className="w-5 h-5 text-blue-500" />,
      'employer_profile_upgrade': <AlertCircle className="w-5 h-5 text-yellow-500" />,
      'employer_approved': <CheckCircle className="w-5 h-5 text-green-500" />,
      'employer_rejected': <XCircle className="w-5 h-5 text-red-500" />,
      'job_approved': <CheckCircle className="w-5 h-5 text-green-500" />,
      'job_rejected': <XCircle className="w-5 h-5 text-red-500" />
    };
    return icons[key] || <FileText className="w-5 h-5 text-gray-500" />;
  };

  const getStatusBadge = (status, opened, clicked) => {
    if (status === 'bounced') return <Badge variant="destructive">Bounced</Badge>;
    if (status === 'failed') return <Badge variant="destructive">Failed</Badge>;
    if (status === 'skipped') return <Badge variant="secondary">Skipped</Badge>;
    if (clicked) return <Badge className="bg-purple-500">Clicked</Badge>;
    if (opened) return <Badge className="bg-blue-500">Opened</Badge>;
    if (status === 'sent') return <Badge className="bg-green-500">Sent</Badge>;
    return <Badge variant="secondary">{status}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', { 
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Mail Settings</h1>
          <p className="text-slate-500">Configure SMTP, templates, and track email delivery</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 max-w-2xl">
          <TabsTrigger value="smtp" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            SMTP
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="tracking" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Tracking
          </TabsTrigger>
          <TabsTrigger value="blocked" className="flex items-center gap-2">
            <Ban className="w-4 h-4" />
            Blocked
          </TabsTrigger>
        </TabsList>

        {/* SMTP Settings Tab */}
        <TabsContent value="smtp" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                SMTP Configuration
              </CardTitle>
              <CardDescription>
                Configure your email server settings for sending notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-base font-medium">Enable Email Notifications</Label>
                  <p className="text-sm text-slate-500">Turn on/off all email sending</p>
                </div>
                <Switch
                  checked={smtpSettings.enabled}
                  onCheckedChange={(checked) => setSmtpSettings({ ...smtpSettings, enabled: checked })}
                />
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="from_name">From Name</Label>
                  <Input
                    id="from_name"
                    value={smtpSettings.from_name}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, from_name: e.target.value })}
                    placeholder="VVVSI Job Portal"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="from_email">From Email</Label>
                  <Input
                    id="from_email"
                    type="email"
                    value={smtpSettings.from_email}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, from_email: e.target.value })}
                    placeholder="support@vvvsi.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smtp_host">SMTP Host</Label>
                  <Input
                    id="smtp_host"
                    value={smtpSettings.smtp_host}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_host: e.target.value })}
                    placeholder="smtp.bizmailserver.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smtp_port">SMTP Port</Label>
                  <Input
                    id="smtp_port"
                    type="number"
                    value={smtpSettings.smtp_port}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_port: parseInt(e.target.value) || 587 })}
                    placeholder="587"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smtp_user">SMTP Username</Label>
                  <Input
                    id="smtp_user"
                    value={smtpSettings.smtp_user}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_user: e.target.value })}
                    placeholder="support@vvvsi.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smtp_password">SMTP Password</Label>
                  <Input
                    id="smtp_password"
                    type="password"
                    value={smtpSettings.smtp_password}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_password: e.target.value })}
                    placeholder="Enter password"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                <Switch
                  checked={smtpSettings.use_tls}
                  onCheckedChange={(checked) => setSmtpSettings({ ...smtpSettings, use_tls: checked })}
                />
                <div>
                  <Label className="text-base font-medium">Use TLS/STARTTLS</Label>
                  <p className="text-sm text-slate-500">Enable for port 587 (STARTTLS), disable for port 465 (SSL)</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="default_cc">Default CC Emails (Optional)</Label>
                <Input
                  id="default_cc"
                  type="text"
                  value={Array.isArray(smtpSettings.default_cc) ? smtpSettings.default_cc.join(', ') : (smtpSettings.default_cc || '')}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, default_cc: e.target.value })}
                  placeholder="cc1@vvvsi.com, cc2@vvvsi.com"
                />
                <p className="text-xs text-slate-500">Separate multiple emails with commas</p>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSaveSmtp} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Test SMTP Connection
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="Enter test email address"
                  className="flex-1"
                />
                <Button onClick={handleTestSmtp} disabled={testing} variant="outline">
                  {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Test Email
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Templates Tab */}
        <TabsContent value="templates" className="space-y-6">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-4">
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Templates</CardTitle>
                    <Button variant="ghost" size="sm" onClick={handleResetDefaults} title="Reset to defaults">
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {templates.map((template) => (
                    <div
                      key={template.key}
                      onClick={() => handleSelectTemplate(template)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors border ${
                        selectedTemplate?.key === template.key
                          ? 'bg-teal-50 border-teal-200'
                          : 'hover:bg-slate-50 border-transparent'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {getTemplateIcon(template.key)}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">{template.name}</p>
                          <p className="text-xs text-slate-500 truncate">{template.description}</p>
                        </div>
                        <Badge variant={template.enabled ? "default" : "secondary"} className="text-xs">
                          {template.enabled ? 'On' : 'Off'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            <div className="col-span-8">
              {editingTemplate ? (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getTemplateIcon(editingTemplate.key)}
                        <div>
                          <CardTitle>{editingTemplate.name}</CardTitle>
                          <CardDescription>{editingTemplate.description}</CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={editingTemplate.enabled}
                          onCheckedChange={(checked) => setEditingTemplate({ ...editingTemplate, enabled: checked })}
                        />
                        <span className="text-sm text-slate-500">{editingTemplate.enabled ? 'Enabled' : 'Disabled'}</span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="subject">Email Subject</Label>
                      <Input
                        id="subject"
                        value={editingTemplate.subject}
                        onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
                      />
                      <p className="text-xs text-slate-500">
                        Variables: {editingTemplate.variables?.map(v => `{${v}}`).join(', ')}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="cc_emails">CC Emails (Optional)</Label>
                      <Input
                        id="cc_emails"
                        type="text"
                        value={Array.isArray(editingTemplate.cc_emails) ? editingTemplate.cc_emails.join(', ') : (editingTemplate.cc_emails || '')}
                        onChange={(e) => setEditingTemplate({ 
                          ...editingTemplate, 
                          cc_emails: e.target.value 
                        })}
                        placeholder="cc1@example.com, cc2@example.com"
                      />
                      <p className="text-xs text-slate-500">Separate multiple emails with commas</p>
                    </div>

                    <div className="p-4 bg-blue-50 rounded-lg">
                      <Label className="text-blue-700 font-medium">Available Variables</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {editingTemplate.variables?.map((variable) => (
                          <Badge key={variable} variant="outline" className="bg-white text-blue-700 border-blue-200">
                            {`{${variable}}`}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Email Body (HTML)</Label>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => setShowCodeEditor(true)}>
                            <Code className="w-4 h-4 mr-1" />
                            Edit Code
                          </Button>
                          <Button variant="outline" size="sm" onClick={handlePreviewTemplate}>
                            <Eye className="w-4 h-4 mr-1" />
                            Preview
                          </Button>
                        </div>
                      </div>
                      <div className="border rounded-lg overflow-hidden">
                        <div className="p-2 bg-slate-100 border-b text-xs text-slate-500">
                          HTML Preview (Click "Edit Code" to modify)
                        </div>
                        <div 
                          className="p-4 h-64 overflow-auto bg-white"
                          dangerouslySetInnerHTML={{ __html: editingTemplate.html_body }}
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t">
                      <Button variant="outline" onClick={() => handleSelectTemplate(selectedTemplate)}>
                        Discard Changes
                      </Button>
                      <Button onClick={handleSaveTemplate} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Save Template
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center h-96 text-center">
                    <FileText className="w-16 h-16 text-slate-300 mb-4" />
                    <h3 className="text-lg font-medium text-slate-700">Select a Template</h3>
                    <p className="text-slate-500">Choose a template from the list to edit</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Tracking Tab */}
        <TabsContent value="tracking" className="space-y-6">
          {/* Stats Cards */}
          {emailStats && (
            <div className="grid grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-green-100 rounded-full">
                      <Send className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{emailStats.total_sent}</p>
                      <p className="text-sm text-slate-500">Emails Sent</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-100 rounded-full">
                      <MailOpen className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{emailStats.total_opened}</p>
                      <p className="text-sm text-slate-500">Opened ({emailStats.open_rate}%)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-purple-100 rounded-full">
                      <MousePointerClick className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{emailStats.total_clicked}</p>
                      <p className="text-sm text-slate-500">Clicked ({emailStats.click_rate}%)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-red-100 rounded-full">
                      <XCircle className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{emailStats.total_bounced}</p>
                      <p className="text-sm text-slate-500">Bounced ({emailStats.bounce_rate}%)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Email Logs Table */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Email Delivery Logs</CardTitle>
                <div className="flex gap-2">
                  <Input
                    placeholder="Search by email..."
                    value={logsFilter}
                    onChange={(e) => setLogsFilter(e.target.value)}
                    className="w-64"
                  />
                  <Button variant="outline" onClick={fetchEmailLogs}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Template</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Sent At</TableHead>
                    <TableHead>Opens</TableHead>
                    <TableHead>Clicks</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {emailLogs.map((log, idx) => (
                    <TableRow key={log.id || idx}>
                      <TableCell className="font-medium">{log.to_email}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{log.template_key}</Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(log.status, log.opened, log.clicked)}</TableCell>
                      <TableCell className="text-sm text-slate-500">{formatDate(log.sent_at || log.created_at)}</TableCell>
                      <TableCell>
                        {log.open_count > 0 ? (
                          <span className="text-blue-600 font-medium">{log.open_count}</span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {log.click_count > 0 ? (
                          <span className="text-purple-600 font-medium">{log.click_count}</span>
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {emailLogs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No email logs found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              
              {logsTotalPages > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={logsPage === 1}
                    onClick={() => setLogsPage(p => p - 1)}
                  >
                    Previous
                  </Button>
                  <span className="px-4 py-2 text-sm">Page {logsPage} of {logsTotalPages}</span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={logsPage === logsTotalPages}
                    onClick={() => setLogsPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Blocked Emails Tab */}
        <TabsContent value="blocked" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Blocked Emails</CardTitle>
                  <CardDescription>
                    Emails that bounced or were manually blocked. These will be skipped when sending.
                  </CardDescription>
                </div>
                <Button onClick={() => setShowBlockDialog(true)}>
                  <Ban className="w-4 h-4 mr-2" />
                  Block Email
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email Address</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Blocked At</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {blockedEmails.map((blocked) => (
                    <TableRow key={blocked.email}>
                      <TableCell className="font-medium">{blocked.email}</TableCell>
                      <TableCell className="max-w-xs truncate">{blocked.reason}</TableCell>
                      <TableCell>
                        <Badge variant={blocked.bounce_type === 'hard' ? 'destructive' : 'secondary'}>
                          {blocked.bounce_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{formatDate(blocked.blocked_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleUnblockEmail(blocked.email)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {blockedEmails.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                        <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-300" />
                        No blocked emails
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              
              {blockedTotalPages > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={blockedPage === 1}
                    onClick={() => setBlockedPage(p => p - 1)}
                  >
                    Previous
                  </Button>
                  <span className="px-4 py-2 text-sm">Page {blockedPage} of {blockedTotalPages}</span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={blockedPage === blockedTotalPages}
                    onClick={() => setBlockedPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Block Email Dialog */}
      <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Block Email Address</DialogTitle>
            <DialogDescription>
              Blocked emails will be skipped when sending notifications.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="block-email">Email Address</Label>
            <Input
              id="block-email"
              type="email"
              value={newBlockedEmail}
              onChange={(e) => setNewBlockedEmail(e.target.value)}
              placeholder="email@example.com"
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBlockDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleBlockEmail} className="bg-red-600 hover:bg-red-700">
              Block Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Code Editor Dialog */}
      <Dialog open={showCodeEditor} onOpenChange={setShowCodeEditor}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="w-5 h-5" />
              Edit HTML Template
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={editingTemplate?.html_body || ''}
              onChange={(e) => setEditingTemplate({ ...editingTemplate, html_body: e.target.value })}
              className="font-mono text-sm h-[50vh] resize-none"
              placeholder="Enter HTML code..."
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCodeEditor(false)}>
              Close
            </Button>
            <Button onClick={() => setShowCodeEditor(false)} className="bg-teal-600 hover:bg-teal-700">
              Apply Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Email Preview
            </DialogTitle>
          </DialogHeader>
          <div className="border rounded-lg overflow-auto h-[60vh] bg-white">
            <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MailSettings;
